import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CreateGroupContentInput,
  GroupContentDetail,
  GroupContentListResponse,
  HydratedArticleItem,
  HydratedGroupItem,
  HydratedVideoItem,
  ListGroupContentsQuery,
  UpdateGroupContentInput,
} from '@news-cms/shared';
import { ApiError, api } from './api';
import { toast } from '@/components/ui/toaster';

function qs(filter: Partial<ListGroupContentsQuery>): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filter)) {
    if (v === undefined || v === null || v === '') continue;
    params.set(k, String(v));
  }
  return params.toString();
}

/* ---------- query options ---------- */

export const groupContentsListQueryOptions = (filter: Partial<ListGroupContentsQuery>) =>
  queryOptions({
    queryKey: ['group-contents', 'list', filter] as const,
    queryFn: () => api<GroupContentListResponse>(`/api/admin/group-contents?${qs(filter)}`),
  });

export const groupContentDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['group-contents', 'detail', id] as const,
    queryFn: () =>
      api<{ group: GroupContentDetail }>(`/api/admin/group-contents/${id}`).then((d) => d.group),
  });

/** Hydrated items for the /homepage inline preview. Server-side hydration
 *  so admin sees exactly what the public site renders. */
export const groupContentPreviewQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['group-contents', 'preview', id] as const,
    queryFn: () =>
      api<{ items: HydratedGroupItem[] }>(`/api/admin/group-contents/${id}/preview`).then(
        (d) => d.items,
      ),
    staleTime: 10_000,
  });

export interface ArticlePickerFilters {
  q?: string;
  categorySlug?: string | null;
  sort?: 'latest' | 'popular';
  /** When true, server only returns posts flagged `is_press_release=1`.
   *  Used by the PR override creation flow. */
  pressReleaseOnly?: boolean;
}

/** Picker for article-manual config editor. */
export const groupContentsArticlePickerQueryOptions = (filters: ArticlePickerFilters = {}) =>
  queryOptions({
    queryKey: [
      'group-contents',
      'article-picker',
      {
        q: filters.q?.trim() ?? '',
        categorySlug: filters.categorySlug ?? null,
        sort: filters.sort ?? 'latest',
        pressReleaseOnly: filters.pressReleaseOnly ?? false,
      },
    ] as const,
    queryFn: () => {
      const params = new URLSearchParams({ limit: '30' });
      if (filters.q?.trim()) params.set('q', filters.q.trim());
      if (filters.categorySlug) params.set('categorySlug', filters.categorySlug);
      if (filters.sort) params.set('sort', filters.sort);
      if (filters.pressReleaseOnly) params.set('pressReleaseOnly', 'true');
      return api<{ items: HydratedArticleItem[] }>(
        `/api/admin/group-contents/_article-picker?${params.toString()}`,
      ).then((d) => d.items);
    },
    staleTime: 30_000,
  });

/** Picker for video-manual config editor. */
export const groupContentsVideoPickerQueryOptions = (q: string, playlistId: string | null) =>
  queryOptions({
    queryKey: ['group-contents', 'video-picker', q.trim(), playlistId] as const,
    queryFn: () => {
      const params = new URLSearchParams({ limit: '30' });
      if (q.trim()) params.set('q', q.trim());
      if (playlistId) params.set('playlistId', playlistId);
      return api<{ items: HydratedVideoItem[] }>(
        `/api/admin/group-contents/_video-picker?${params.toString()}`,
      ).then((d) => d.items);
    },
    staleTime: 30_000,
  });

/** YouTube playlists — feeds the playlist filter dropdown. Static-ish so
 *  staleTime is generous. */
export const groupContentsVideoPlaylistsQueryOptions = queryOptions({
  queryKey: ['group-contents', 'video-playlists'] as const,
  queryFn: () =>
    api<{ items: { id: string; slug: string; name: string }[] }>(
      '/api/admin/group-contents/_video-playlists',
    ).then((d) => d.items),
  staleTime: 5 * 60_000,
});

/* ---------- slug check ---------- */

export interface SlugCheckResult {
  valid: boolean;
  available: boolean;
  reason: 'empty' | 'invalid' | null;
}

export async function checkGroupSlug(slug: string, excludeId?: string): Promise<SlugCheckResult> {
  const params = new URLSearchParams({ slug });
  if (excludeId) params.set('excludeId', excludeId);
  return api<SlugCheckResult>(`/api/admin/group-contents/_slug-check?${params.toString()}`);
}

/* ---------- mutations ---------- */

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ['group-contents', 'list'] });
  // Preview hydration changes whenever the group config changes — bust it
  // so inline /homepage editors re-render with fresh items.
  void qc.invalidateQueries({ queryKey: ['group-contents', 'preview'] });
  // Homepage layout hydration depends on group contents — bust the editor's
  // preview query too so /homepage shows fresh items right after a group edit.
  void qc.invalidateQueries({ queryKey: ['homepage'] });
}

export function useCreateGroupContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateGroupContentInput) => {
      const data = await api<{ group: GroupContentDetail }>('/api/admin/group-contents', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      return data.group;
    },
    onSuccess: (group) => {
      qc.setQueryData(groupContentDetailQueryOptions(group.id).queryKey, group);
      invalidateAll(qc);
      toast.success('สร้าง Group Content เรียบร้อย');
    },
    onError: (e) => toast.error(groupContentErrorMessage(e)),
  });
}

export function useUpdateGroupContent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateGroupContentInput) => {
      const data = await api<{ group: GroupContentDetail }>(`/api/admin/group-contents/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      });
      return data.group;
    },
    onSuccess: (group) => {
      qc.setQueryData(groupContentDetailQueryOptions(group.id).queryKey, group);
      invalidateAll(qc);
      toast.success('บันทึก Group Content เรียบร้อย');
    },
    onError: (e) => toast.error(groupContentErrorMessage(e)),
  });
}

export function useDeleteGroupContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api<{ deleted: true }>(`/api/admin/group-contents/${id}`, { method: 'DELETE' });
      return id;
    },
    onSuccess: (id) => {
      qc.removeQueries({ queryKey: ['group-contents', 'detail', id] });
      invalidateAll(qc);
      toast.success('ลบ Group Content เรียบร้อย');
    },
    onError: (e) => toast.error(groupContentErrorMessage(e)),
  });
}

/* ---------- error messages ---------- */

export function groupContentErrorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return 'เกิดข้อผิดพลาด กรุณาลองใหม่';
  switch (e.code) {
    case 'VALIDATION_ERROR':
      return 'ข้อมูลไม่ถูกต้อง — กรุณาตรวจฟอร์ม';
    case 'SLUG_CONFLICT':
      return 'slug นี้ถูกใช้แล้ว';
    case 'SLUG_IN_USE':
      return 'ไม่สามารถเปลี่ยน slug ได้ — มี slot ใน /homepage อ้างถึงอยู่';
    case 'GROUP_IN_USE':
      return 'ไม่สามารถลบได้ — มี slot ใน /homepage อ้างถึงอยู่';
    case 'NOT_FOUND':
      return 'ไม่พบ Group Content';
    case 'FORBIDDEN':
      return 'ไม่มีสิทธิ์ — ต้องมี settings.update';
    default:
      return e.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่';
  }
}
